import { useEffect, useMemo, useRef, type DependencyList } from "react";

import {
  askChoice as askChoiceGlobal,
  askConfirm as askConfirmGlobal,
  askConfirmWithChecks as askConfirmWithChecksGlobal,
} from "./confirmDialog";

/** Confirmaciones que pertenecen a la pantalla (o al panel) que las pidió.
 *
 * El diálogo es global: sin esto seguía abierto tras salir con Atrás o tras
 * cambiar de torneo, categoría, jugador o sede, y al aceptarlo la acción caía
 * sobre lo que ya se había dejado (bloqueo P1 del PR #601). Con este gancho,
 * al desmontar o al cambiar cualquiera de `deps` la pregunta se retira y
 * contesta «no», así que el manejador sale sin llamar a la API.
 *
 * Uso: `const { askConfirm } = useAbortableConfirm([tournamentId]);` en lugar
 * de importar `askConfirm` de `confirmDialog`. Quien siga importando las
 * funciones globales sin `signal` conserva la conducta de siempre.
 *
 * Si el manejador espera algo ANTES de preguntar, o entre el «sí» y la API,
 * debe tomar `currentSignal()` al empezar y revisar `aborted` antes de
 * actuar: la señal que se lee tarde ya sería la del contexto nuevo. */
export interface AbortableConfirm {
  askConfirm: typeof askConfirmGlobal;
  askChoice: typeof askChoiceGlobal;
  askConfirmWithChecks: typeof askConfirmWithChecksGlobal;
  /** Señal del contexto en pantalla en este momento. */
  currentSignal: () => AbortSignal | undefined;
}

export function useAbortableConfirm(deps: DependencyList): AbortableConfirm {
  const controllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    // StrictMode monta, desmonta y vuelve a montar: el segundo efecto deja un
    // controlador nuevo y sin abortar, así que el flujo normal sigue vivo.
    const controller = new AbortController();
    controllerRef.current = controller;
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return useMemo<AbortableConfirm>(() => {
    const currentSignal = () => controllerRef.current?.signal;
    return {
      askConfirm: (message, options) => askConfirmGlobal(message, { ...options, signal: options?.signal ?? currentSignal() }),
      askChoice: (message, options) => askChoiceGlobal(message, { ...options, signal: options.signal ?? currentSignal() }),
      askConfirmWithChecks: (message, options) =>
        askConfirmWithChecksGlobal(message, { ...options, signal: options.signal ?? currentSignal() }),
      currentSignal,
    };
  }, []);
}
